import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect } from "react";
import { User } from "./types";
import { Loading } from "@/components/ui/loading";
import { Position } from "@/services/positionService";
import { SeniorityLevel } from "../seniority/ManageSeniority";
import { BasicInformationForm } from "./components/BasicInformationForm";
import { CertificationsForm } from "./components/CertificationsForm";
import { DocumentsForm } from "./components/DocumentsForm";
import { RoleSelectionForm } from "./components/RoleSelectionForm";
import { NotificationPreferencesForm } from "./components/NotificationPreferencesForm";
import { BilingualForm } from "./components/BilingualForm";
import { AdminPasswordChangeForm } from "./components/AdminPasswordChangeForm";
import { EmployeeActionsMenu } from "./components/EmployeeActionsMenu";
import { ReferralInfoForm } from "./components/ReferralInfoForm";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useEmployeeForm } from "./hooks/useEmployeeForm";
import { StatusSelectionForm } from "./components/StatusSelectionForm";
import { UniformBadgeInfoForm } from "./components/UniformBadgeInfoForm";
import { UserNotesForm } from "./components/UserNotesForm";

export default function EditEmployee() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: employee, isLoading } = useQuery({
    queryKey: ["employee", id],
    queryFn: async (): Promise<User> => {
      const response = await axiosInstance.get(`user/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: positions } = useQuery({
    queryKey: ["positions", id],
    queryFn: async (): Promise<Position[]> => {
      const response = await axiosInstance.get(`position`);
      return response.data;
    },
    enabled: !!id,
  });

  const { data: seniorityLevels } = useQuery({
    queryKey: ["seniorityLevels", id],
    queryFn: async (): Promise<SeniorityLevel[]> => {
      const response = await axiosInstance.get(`seniority`);
      return response.data;
    },
    enabled: !!id,
  });

  const { form, loading, setFormValues, onSubmit } = useEmployeeForm(
    id,
    employee,
  );

  useEffect(() => {
    setFormValues();
  }, [employee, seniorityLevels, positions]);

  if (isLoading) {
    return (
      <div className="container max-w-2xl mx-auto py-10 px-4">
        <h1 className="text-3xl font-bold text-foreground mb-8">
          Edit Employee
        </h1>
        <Card className="shadow-md">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2 p-4">
              <Skeleton className="h-4 w-[100px]" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-foreground">Edit Employee</h1>
        {employee && (
          <EmployeeActionsMenu
            userId={employee.id}
            employeeName={`${employee.firstName} ${employee.lastName}`}
            employeeEmail={employee.email}
          />
        )}
      </div>
      <div className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <Card>
              <BasicInformationForm
                control={form.control}
                seniorityLevels={seniorityLevels}
              />
              <StatusSelectionForm control={form.control} />
              <CertificationsForm
                control={form.control}
                positions={positions}
              />
              <DocumentsForm control={form.control} positions={positions} />
              <BilingualForm control={form.control} />
              <NotificationPreferencesForm control={form.control} />
              <UniformBadgeInfoForm control={form.control} />
              <RoleSelectionForm control={form.control} />
              <UserNotesForm control={form.control} />
              <ReferralInfoForm control={form.control} currentUserId={id} />
            </Card>
            {loading ? (
              <Loading />
            ) : (
              <div className="flex gap-4">
                <Button type="submit" className="flex-1">
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate("/admin/employees")}
                >
                  Cancel
                </Button>
              </div>
            )}
          </form>
        </Form>

        <Card>
          <AdminPasswordChangeForm userId={id!} />
        </Card>
      </div>
    </div>
  );
}
